import moment from 'moment';
import { createDentallyAppointmentAsync } from './dentallyApi';
import { resolveDentallyBookingAppointment } from './dentallyAppointmentResolver';

const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'center' };

/**
 * Sync a CRM lead's appointment to Dentally.
 * Resolves the exact persisted CRM appointment, converts to ISO, and calls Dentally.
 *
 * @returns {Promise<boolean>} true on success
 */
export async function syncDentallyAppointment({ practiceId, leadId, leadDetail, bookingFingerprint, enqueueSnackbar }) {
    const resolution = resolveDentallyBookingAppointment({
        lead: leadDetail,
        fingerprint: bookingFingerprint,
    });
    if (resolution.outcome === 'ambiguous') {
        enqueueSnackbar('Multiple matching CRM appointments were found. Dentally was not contacted.', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }
    if (resolution.outcome !== 'unique') {
        enqueueSnackbar('The exact CRM appointment for Dentally sync could not be found', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }

    const target = resolution.appointment;

    const parsedTarget = moment(target.datetime, ['DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true);
    if (!parsedTarget.isValid()) {
        enqueueSnackbar('The CRM appointment has an invalid date and cannot be synced', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }

    const patientId = bookingFingerprint?.patientId || null;
    const practitionerId = bookingFingerprint?.practitionerId || null;

    if (!practitionerId) {
        enqueueSnackbar('Missing Dentally practitioner — please rebook the appointment via Dentally', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }

    // 3. Convert CRM datetime → ISO
    const startISO = parsedTarget.toISOString();
    const parsedFinish = moment(bookingFingerprint?.finishTime, moment.ISO_8601, true);
    const finishISO = parsedFinish.isValid()
        ? parsedFinish.toISOString()
        : parsedTarget.clone().add(30, 'minutes').toISOString();

    // 4. Call Dentally API
    const payload = {
        lead_id: leadId,
        crm_appointment_id: target.id,
        start_time: startISO,
        finish_time: finishISO,
        practitioner_id: practitionerId,
        reason: 'Exam',
        notes: 'Appointment created from DentalSem CRM',
    };
    if (patientId) payload.patient_id = patientId;

    const res = await createDentallyAppointmentAsync(practiceId, payload);

    if (res?.ok || res?.status === 200 || res?.status === 201) {
        enqueueSnackbar('Dentally appointment synced successfully', { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR });
        return true;
    }

    const errorMsg = typeof res?.data === 'string'
        ? res.data
        : res?.data?.detail || res?.data?.message || res?.data?.lead_id || 'Failed to sync Dentally appointment';
    enqueueSnackbar(errorMsg, { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
    return false;
}
